import React, { useState } from 'react';
import { Star, User, Loader2, AlertCircle, ListChecks } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useVendorReviews } from '../../services/vendorService';
import CustomSelect from '../../components/shared/CustomSelect';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

const RATING_FILTERS = [
 { key: 'all', label: 'All' },
 { key: '5', label: '5★' },
 { key: '4', label: '4★' },
 { key: '3', label: '3★' },
 { key: '2', label: '2★' },
 { key: '1', label: '1★' },
];

const SORT_OPTIONS = [
 { value: 'newest', label: 'Newest first' },
 { value: 'oldest', label: 'Oldest first' },
 { value: 'highest', label: 'Highest rating' },
 { value: 'lowest', label: 'Lowest rating' },
];

const VendorReviews = () => {
 const { user } = useAuth();
 const vendorId = user?.vendor_profile?.id || user?.id;
 const [filter, setFilter] = useState('all');
 const [sortBy, setSortBy] = useState('newest');

 const { data: reviewsResponse, isLoading, error } = useVendorReviews(vendorId, {
 per_page: 50,
 });

 const paginatedData = reviewsResponse?.data;
 const reviews = Array.isArray(paginatedData?.data)
 ? paginatedData.data
 : Array.isArray(paginatedData)
 ? paginatedData
 : [];

 const filteredAndSortedReviews = reviews
 .filter((review) => {
 if (filter === 'all') return true;
 return Math.floor(review.rating) === parseInt(filter, 10);
 })
 .sort((a, b) => {
 switch (sortBy) {
 case 'newest':
 return new Date(b.created_at) - new Date(a.created_at);
 case 'oldest':
 return new Date(a.created_at) - new Date(b.created_at);
 case 'highest':
 return b.rating - a.rating;
 case 'lowest':
 return a.rating - b.rating;
 default:
 return 0;
 }
 });

 const averageRating =
 reviews.length > 0
 ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length
 : 0;

 const ratingDistribution = [5, 4, 3, 2, 1].map((rating) => ({
 rating,
 count: reviews.filter((r) => Math.floor(r.rating) === rating).length,
 percentage:
 reviews.length > 0
 ? (reviews.filter((r) => Math.floor(r.rating) === rating).length / reviews.length) * 100
 : 0,
 }));

 const emptyCopy =
 filter === 'all'
 ? "You don't have any reviews yet. Ratings appear after clients complete bookings."
 : `No reviews found with ${filter} star${filter !== '1' ? 's' : ''}. Try another filter.`;

 return (
 <div className="space-y-5">
 <VendorPageHeader
 title="Reviews"
 subtitle="See client ratings after completed bookings and respond when a reply is available."
 />

 <VendorGuideCallout
 title={VENDOR_PAGE_GUIDES.reviews.title}
 steps={VENDOR_PAGE_GUIDES.reviews.steps}
 icon={ListChecks}
 />

 {/* Stats */}
 <div className="rounded-lg border border-border bg-surface shadow-sm p-4 sm:p-6">
 {isLoading ? (
 <div className="py-10 text-center">
 <Loader2 className="w-7 h-7 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading review stats…</p>
 </div>
 ) : error ? (
 <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
 <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
 <p className="text-sm text-red-700">
 Failed to load review stats. Please try again later.
 </p>
 </div>
 ) : (
 <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
 <div className="flex items-center gap-6 sm:gap-8">
 <div className="text-center sm:text-left">
 <div className="flex items-center justify-center sm:justify-start gap-1.5 mb-1.5">
 <Star className="w-6 h-6 text-amber-500 fill-amber-500" />
 <span className="text-3xl font-bold tracking-tight text-ink tabular-nums">
 {averageRating.toFixed(1)}
 </span>
 </div>
 <p className="text-sm text-ink-tertiary">Average rating</p>
 </div>
 <div className="h-12 w-px bg-gray-200 hidden sm:block" />
 <div className="text-center sm:text-left">
 <div className="text-3xl font-bold tracking-tight text-ink tabular-nums mb-1.5">
 {reviews.length}
 </div>
 <p className="text-sm text-ink-tertiary">Total reviews</p>
 </div>
 </div>

 <div className="flex-1 max-w-md w-full">
 <h3 className="text-sm font-semibold text-ink mb-3">
 Rating distribution
 </h3>
 <div className="space-y-2">
 {ratingDistribution.map(({ rating, count, percentage }) => (
 <div key={rating} className="flex items-center gap-3">
 <div className="flex items-center gap-1 w-14 shrink-0">
 <span className="text-sm font-medium text-ink-secondary tabular-nums">
 {rating}
 </span>
 <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
 </div>
 <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
 <div
 className="bg-amber-500 h-2 rounded-full transition-all"
 style={{ width: `${percentage}%` }}
 />
 </div>
 <span className="text-sm text-ink-tertiary w-8 text-right tabular-nums">
 {count}
 </span>
 </div>
 ))}
 </div>
 </div>
 </div>
 )}
 </div>

 {/* Filters + list */}
 <div className="rounded-lg border border-border bg-surface shadow-sm overflow-hidden">
 <div className="border-b border-border px-4 py-3 sm:px-5">
 <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
 <div
 className="inline-flex flex-wrap rounded-xl bg-gray-100 p-1 border border-border"
 role="tablist"
 aria-label="Filter by rating"
 >
 {RATING_FILTERS.map((tab) => {
 const isActive = filter === tab.key;
 return (
 <button
 key={tab.key}
 type="button"
 role="tab"
 aria-selected={isActive}
 onClick={() => setFilter(tab.key)}
 className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${ isActive ? 'bg-surface text-ink shadow-sm' : 'text-ink-secondary hover:text-gray-900' }`}
 >
 {tab.label}
 </button>
 );
 })}
 </div>

 <div className="w-full sm:w-48">
 <CustomSelect
 value={sortBy}
 onChange={(value) => setSortBy(value)}
 options={SORT_OPTIONS}
 size="md"
 className="w-full"
 aria-label="Sort reviews"
 />
 </div>
 </div>
 </div>

 <div className="p-4 sm:p-5">
 {isLoading ? (
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-ink-tertiary">Loading reviews…</p>
 </div>
 ) : error ? (
 <div className="rounded-xl border border-red-200 bg-red-50 text-red-600 px-4 py-3 text-sm flex items-start gap-2.5">
 <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
 <span>Failed to load reviews. Please try again later.</span>
 </div>
 ) : filteredAndSortedReviews.length === 0 ? (
 <div className="py-16 sm:py-20 text-center px-4">
 <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-surface-muted text-gray-400">
 <Star className="w-7 h-7" />
 </div>
 <h3 className="text-base font-semibold text-ink">
 No reviews found
 </h3>
 <p className="mt-1.5 text-sm text-ink-tertiary max-w-md mx-auto leading-relaxed">
 {emptyCopy}
 </p>
 </div>
 ) : (
 <div className="space-y-3">
 {filteredAndSortedReviews.map((review) => (
 <div
 key={review.id}
 className="product-surface-muted p-3 sm:p-4 transition-colors hover:border-ink"
 >
 <div className="flex items-start gap-3 sm:gap-4">
 <div className="flex-shrink-0">
 {review.client?.profile_image_url ? (
 <img
 src={review.client.profile_image_url}
 alt={review.client.name || 'Client'}
 className="w-11 h-11 rounded-full object-cover border border-border"
 />
 ) : (
 <div className="w-11 h-11 rounded-full border border-border bg-surface flex items-center justify-center">
 <User className="w-5 h-5 text-gray-400" />
 </div>
 )}
 </div>

 <div className="flex-1 min-w-0">
 <div className="flex items-start justify-between gap-3 mb-1.5">
 <div className="min-w-0">
 <h3 className="text-sm font-semibold text-ink truncate">
 {review.client?.name || 'Anonymous'}
 </h3>
 <div className="flex items-center gap-2 mt-1">
 <div className="flex items-center">
 {[1, 2, 3, 4, 5].map((star) => (
 <Star
 key={star}
 className={`w-3.5 h-3.5 ${ star <= review.rating ? 'text-amber-500 fill-amber-500' : 'text-gray-300' }`}
 />
 ))}
 </div>
 <span className="text-sm text-ink-secondary tabular-nums">
 {Number(review.rating).toFixed(1)}
 </span>
 </div>
 </div>
 <span className="text-xs text-ink-tertiary flex-shrink-0 tabular-nums">
 {new Date(review.created_at).toLocaleDateString('en-US', {
 month: 'short',
 day: 'numeric',
 year: 'numeric',
 })}
 </span>
 </div>

 {review.comment && (
 <p className="text-sm text-ink-secondary mt-2 leading-relaxed">
 {review.comment}
 </p>
 )}

 {review.booking && (
 <p className="text-xs text-ink-tertiary mt-2.5">
 Service:{' '}
 <span className="font-medium text-ink-secondary">
 {review.booking.service?.service_name || 'N/A'}
 </span>
 </p>
 )}
 </div>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 </div>
 </div>
 );
};

export default VendorReviews;
